import { useState, useEffect, useMemo, useRef } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import {
  Palette, Code2, Smartphone, PenLine, Megaphone, LayoutGrid, Film, Search,
  Sun, Moon, Sparkles, Settings2, RefreshCw, Zap, Check, Languages, MoreVertical, FileDown,
  Calculator as Calculator2, Wallet, Plug,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Toaster, toast } from "sonner";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { dict, SERVICE_LIST } from "@/lib/i18n";
import { DEFAULT_RATES, computePricing, fmt } from "@/lib/pricing";
import { CALC } from "@/constants/testIds/calc";
import { LogoLockup } from "@/components/Logo";
import IntegrationsModal from "@/components/IntegrationsModal";
import Footer from "@/components/Footer";
import PaymentsModal from "@/components/PaymentsModal";
import AccountModal from "@/components/AccountModal";
import Blog from "@/components/Blog";
import ProposalModal from "@/components/ProposalModal";
import EarningsCalculator from "@/components/EarningsCalculator";
import AboutCalculator from "@/components/AboutCalculator";
import SuccessStories from "@/components/SuccessStories";
import IntegrationCTA from "@/components/IntegrationCTA";
import PricingPlans from "@/components/PricingPlans";
import HelpfulResources from "@/components/HelpfulResources";
import RatingFeedback from "@/components/RatingFeedback";
import { loadReviewSchema } from "@/components/RatingSummary";
import BonusFeedbackModal from "@/components/BonusFeedbackModal";
import ExitIntentModal from "@/components/ExitIntentModal";
import VisitorCounter from "@/components/VisitorCounter";
import HeroQuickStart from "@/components/HeroQuickStart";
import HeroIntro from "@/components/HeroIntro";
import SeoFaq from "@/components/SeoFaq";
import { CalcStepper, OtherServiceField, UnitHint, ScopeDetails, AiPanel, scopeToLines } from "@/components/CalcSteps";
import { track } from "@/lib/track";
import { Link as RouterLink } from "react-router-dom";
import { LANDING_PAGES, LANDING_SLUGS } from "@/data/landingPages";
import { CURRENCIES, formatMoney, convertFromSar } from "@/lib/currency";
import { normalizeDigits, localizeDigits } from "@/lib/digits";
import { Share2, ShoppingCart, User, Coins, Crown, Gift, ArrowLeft, X, UserPlus, Bookmark, Download } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useAuth, authAxios } from "@/context/AuthContext";
import AuthModal from "@/components/AuthModal";
import InstallPWA, { usePWAInstall, InstallHelp, InstalledShare } from "@/components/InstallPWA";
import ScrollTopFab from "@/components/ScrollTopFab";
import CurrencySwitcher from "@/components/CurrencySwitcher";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ICONS = { Palette, Code2, Smartphone, PenLine, Megaphone, LayoutGrid, Film, Search };

// URL params: ?lang=en|ar and pathname `/embed` for chromeless embed mode
const _urlParams = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
const _urlLang = _urlParams.get("lang");
const IS_EMBED = typeof window !== "undefined" && window.location.pathname.startsWith("/embed");
const SAVED = (() => {
  try { return IS_EMBED ? {} : JSON.parse(localStorage.getItem("pl_project") || "{}"); } catch { return {}; }
})();

/* Sticky horizontal tool navigation.
   Shows 3 tool anchors (Pricing / Earnings / Integrations) and — when the PWA
   install prompt is available (Android/Chrome/Edge) or on iOS Safari — a 4th
   "Install app" pill. Clicking triggers the native prompt or the iOS modal. */
function ToolsNav({ dark, lang, t }) {
  const { canInstall, isIOS, isAndroid, standalone, install } = usePWAInstall();
  const [helpOpen, setHelpOpen] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  const items = [
    { id: "pricing-grid",   ar: "حاسبة التسعير",   en: "Pricing Calculator", icon: Calculator2 },
    { id: "earnings-tool",  ar: "حاسبة الدخل",     en: "Earnings Calculator", icon: Wallet },
    { id: "integrate-tool", ar: "أدوات التكامل",   en: "Integration Tools", icon: Plug },
  ];
  const [active, setActive] = useState("pricing-grid");
  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((en) => en.isIntersecting && setActive(en.target.id)),
      { rootMargin: "-40% 0px -55% 0px" },
    );
    items.forEach((it) => { const el = document.getElementById(it.id); if (el) obs.observe(el); });
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onInstallClick = async (e) => {
    e.preventDefault();
    if (standalone) {
      setShareOpen(true);
      return;
    }
    if (canInstall) {
      const ok = await install();
      if (!ok) setHelpOpen(true);
    } else {
      setHelpOpen(true);
    }
  };

  const openShare = (e) => {
    e.preventDefault();
    setShareOpen(true);
  };

  const platform = isIOS ? "ios" : isAndroid ? "android" : "generic";
  const installLabel = standalone
    ? (lang === "ar" ? "مشاركة" : "Share")
    : (lang === "ar" ? "تثبيت التطبيق" : "Install app");

  return (
    <>
      <nav
        data-testid="tools-nav"
        dir={t.dir}
        className={`sticky z-30 border-b ${dark ? "bg-[#182138]/95 border-[#2e3a5c]" : "bg-white/95 border-[#e5e5ea]"} backdrop-blur-md`}
        style={{ top: "calc(env(safe-area-inset-top) + 64px)" }}
      >
        <div className="max-w-6xl mx-auto px-2 sm:px-6 lg:px-8 py-2 flex items-center justify-start sm:justify-center gap-1.5 sm:gap-2 overflow-x-auto no-scrollbar">
          <div role="tablist" aria-label={lang === "ar" ? "التنقل بين الأدوات" : "Tools navigation"} data-testid="toolnav-tabs" className={`shrink-0 inline-flex items-center gap-0.5 p-1 rounded-full border ${dark ? "bg-[#1b2540] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]"}`}>
          {items.map((it) => {
            const on = active === it.id;
            const Icon = it.icon;
            return (
            <a
              key={it.id}
              href={`#${it.id}`}
              role="tab"
              aria-selected={on}
              aria-current={on ? "location" : undefined}
              data-testid={`toolnav-${it.id}`}
              onClick={(e) => {
                e.preventDefault();
                setActive(it.id);
                const el = document.getElementById(it.id);
                if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
              }}
              className={`relative shrink-0 inline-flex items-center gap-1.5 px-2.5 sm:px-4 h-8 sm:h-9 rounded-full text-[11px] sm:text-sm font-extrabold transition-[background-color,color,box-shadow] duration-200 whitespace-nowrap ${
                on
                  ? "bg-[#18c98f] text-white shadow-[0_4px_14px_-4px_rgba(24,201,143,0.6)]"
                  : dark
                  ? "text-white/70 hover:text-white hover:bg-white/5"
                  : "text-[#182138]/70 hover:text-[#182138] hover:bg-black/5"
              } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
            >
              <Icon className="hidden sm:block h-3.5 w-3.5 shrink-0" strokeWidth={2.4} />
              {lang === "ar" ? it.ar : it.en}
            </a>
            );
          })}
          </div>
          {/* Dedicated Share button — icon-only on mobile, icon+text on desktop */}
          <button
            type="button"
            onClick={openShare}
            data-testid="toolnav-share"
            aria-label={lang === "ar" ? "مشاركة" : "Share"}
            title={lang === "ar" ? "مشاركة" : "Share"}
            className={`shrink-0 inline-flex items-center gap-1.5 justify-center w-8 sm:w-auto h-8 sm:h-9 sm:px-5 rounded-full text-xs sm:text-sm font-extrabold transition-all whitespace-nowrap border ${
              dark
                ? "bg-[#1f2a47] border-[#18c98f]/50 text-[#18c98f] hover:bg-[#18c98f] hover:text-[#182138]"
                : "bg-white border-[#18c98f]/50 text-[#12a777] hover:bg-[#18c98f] hover:text-white"
            } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
            style={{ boxShadow: "0 0 14px -4px rgba(24,201,143,0.4)" }}
          >
            <Share2 className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
            <span className="hidden sm:inline">{lang === "ar" ? "مشاركة" : "Share"}</span>
          </button>
          {/* Install button — icon-only on mobile, icon+text on desktop; hidden when installed */}
          {!standalone && (
            <button
              type="button"
              onClick={onInstallClick}
              data-testid="toolnav-install"
              aria-label={installLabel}
              title={installLabel}
              className={`shrink-0 inline-flex items-center gap-1.5 justify-center w-8 sm:w-auto h-8 sm:h-9 sm:px-5 rounded-full text-xs sm:text-sm font-extrabold transition-all whitespace-nowrap border bg-[#18c98f] text-white border-[#18c98f] hover:brightness-110 ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
              style={{ boxShadow: "0 0 18px -4px rgba(24,201,143,0.55)" }}
            >
              <Download className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline">{installLabel}</span>
            </button>
          )}
        </div>
      </nav>
      <InstallHelp open={helpOpen} onOpenChange={setHelpOpen} lang={lang} theme={dark ? "dark" : "light"} platform={platform} />
      <InstalledShare open={shareOpen} onOpenChange={setShareOpen} lang={lang} theme={dark ? "dark" : "light"} />
    </>
  );
}

export default function Calculator({ initialAnchor } = {}) {
  const [lang, setLang] = useState(() => _urlLang === "ar" || _urlLang === "en" ? _urlLang : (localStorage.getItem("pl_lang") || "ar"));
  const [theme, setTheme] = useState(() => localStorage.getItem("pl_theme") || "dark");
  // Digit style is tied to the language — Arabic UI shows ١٢٣, English shows 123.
  // Inputs always normalize via `normalizeDigits` so users can type either style.
  const digits = lang === "ar" ? "ar" : "en";
  const [service, setService] = useState(() => SAVED.service || "web_dev");
  const [experience, setExperience] = useState(() => SAVED.experience || "intermediate");
  const [complexity, setComplexity] = useState(() => SAVED.complexity || "medium");
  const [unit, setUnit] = useState(() => SAVED.unit || "hour");
  const [quantity, setQuantity] = useState(() => SAVED.quantity ?? 10);
  const [revisions, setRevisions] = useState(() => SAVED.revisions ?? 2);
  const [rush, setRush] = useState(() => !!SAVED.rush);
  const [description, setDescription] = useState(() => SAVED.description || "");
  const [customServiceName, setCustomServiceName] = useState(() => SAVED.customServiceName || "");
  const [otherOpen, setOtherOpen] = useState(() => !!SAVED.customServiceName);
  const [scopeDetails, setScopeDetails] = useState(() => SAVED.scopeDetails || {});
  const [aiError, setAiError] = useState(null);
  const [aiQuota, setAiQuota] = useState(null);
  const aiReqIdRef = useRef(null);
  const [customRates, setCustomRates] = useState(() => {
    const s = localStorage.getItem("pl_rates");
    return s ? JSON.parse(s) : DEFAULT_RATES;
  });
  const [ratesOpen, setRatesOpen] = useState(false);
  const [editingRates, setEditingRates] = useState(customRates);
  const [integrateOpen, setIntegrateOpen] = useState(false);
  const [paymentsOpen, setPaymentsOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [accountMode, setAccountMode] = useState("user"); // user | admin
  const [authModalOpen, setAuthModalOpen] = useState(false);
  const [authModalMode, setAuthModalMode] = useState("login");
  const navigate = useNavigate();
  const { user: authUser, loading: authLoading, logout: authLogout, refresh } = useAuth();
  const [proposalOpen, setProposalOpen] = useState(false);
  const [email, setEmail] = useState(() => localStorage.getItem("pl_email") || "");
  const [paidCredits, setPaidCredits] = useState(0);
  const [subscription, setSubscription] = useState(null); // {plan, active, expires_at, days_remaining}
  const [currency, setCurrency] = useState(() => localStorage.getItem("pl_currency") || "SAR");
  const [ai, setAi] = useState(() => SAVED.ai || null);
  const [aiLoading, setAiLoading] = useState(false);
  const [finalPrice, setFinalPrice] = useState(() => SAVED.finalPrice ?? null);
  const [priceEdited, setPriceEdited] = useState(() => !!SAVED.priceEdited);
  const [rateInfo, setRateInfo] = useState(null); // {used, remaining, limit}
  const [rateBlocked, setRateBlocked] = useState(null); // {message_ar, message_en, limit}
  const [bonusOpen, setBonusOpen] = useState(false);
  const [bonusClaimed, setBonusClaimed] = useState(() => localStorage.getItem("pl_bonus_claimed") === "1");
  const [exitOpen, setExitOpen] = useState(false);

  // Scroll back to the LCD when the AI finishes and a new result lands.
  useEffect(() => {
    if (ai) {
      setTimeout(scrollToPricingLCD, 150);
    }
  }, [ai]);

  // Pricing LCD ref + helper that scrolls it into view. The LCD only auto-
  // centers after the explicit "احسب الآن" button is pressed (via `ai` effect
  // above) — NOT on every input/toggle change, per user request.
  const pricingLcdRef = useRef(null);
  const scrollToPricingLCD = () => {
    pricingLcdRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  };
  const [exitVariant, setExitVariant] = useState("subscribe"); // subscribe | yearly | share
  const yearlyOnRef = useRef(false);
  useEffect(() => {
    if (IS_EMBED) return;
    axios.get(`${API}/payments/packages`).then((r) => {
      yearlyOnRef.current = (r.data.packages || []).some((p) => p.id === "sub_yearly");
    }).catch(() => {});
  }, []);

  useEffect(() => { if (!IS_EMBED) { track("visit"); loadReviewSchema(); } }, []);
  const inputsKey = JSON.stringify([service, experience, complexity, unit, quantity, revisions, rush, description, customServiceName]);
  const initialInputsKey = useRef(inputsKey);
  useEffect(() => {
    if (inputsKey === initialInputsKey.current) return;
    track("calc_start");
    aiReqIdRef.current = null;
    setAiError(null);
    // An AI price no longer matches once the inputs change.
    setAi(null);
    setPriceEdited(false);
    setFinalPrice(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inputsKey]);
  // Keep the project across sign-in redirects, payment returns and reloads.
  useEffect(() => {
    try {
      localStorage.setItem("pl_project", JSON.stringify({
        service, experience, complexity, unit, quantity, revisions, rush, description,
        customServiceName, scopeDetails, ai, finalPrice, priceEdited,
      }));
    } catch {}
  }, [service, experience, complexity, unit, quantity, revisions, rush, description, customServiceName, scopeDetails, ai, finalPrice, priceEdited]);
  useEffect(() => { if (authModalOpen) track("signup_open"); }, [authModalOpen]);
  useEffect(() => { if (proposalOpen) track("proposal_open", null, { once: false }); }, [proposalOpen]);
  const openBuy = (from) => { track("buy_open", { from }, { once: false }); setPaymentsOpen(true); };
  const quickPick = (id) => {
    setService(id);
    setUnit("project");
    setQuantity(1);
    track("calc_start");
    setTimeout(() => document.getElementById("pricing-grid")?.scrollIntoView({ behavior: "smooth", block: "start" }), 120);
  };
  const startPricing = () => {
    document.getElementById("pricing-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const quotaHint = () => !aiQuota
    ? t.ai.pricingHint
    : aiQuota.next_cost === "credit"
    ? (lang === "ar" ? `رصيد AI: ${aiQuota.credits}` : `AI credits: ${aiQuota.credits}`)
    : aiQuota.next_cost === "subscription"
    ? (lang === "ar" ? "اشتراك AI نشط" : "AI subscription active")
    : (lang === "ar" ? `متبقٍ ${aiQuota.free_remaining.toLocaleString("ar-SA")} من ${aiQuota.free_limit.toLocaleString("ar-SA")} مجانًا` : `AI: ${aiQuota.free_remaining}/${aiQuota.free_limit} free left`);

  const t = dict[lang];

  useEffect(() => {
    document.documentElement.setAttribute("dir", t.dir);
    document.documentElement.setAttribute("lang", lang);
    localStorage.setItem("pl_lang", lang);
    // Update <title> + meta description dynamically for SEO (per language and route anchor)
    const isPricing = initialAnchor === "pricing";
    if (lang === "ar") {
      document.title = isPricing
        ? "باقات اشتراك حاسبة برولانسر — 30 اقتراحًا بـ 19 ر.س · شهري 99 ر.س لامحدود"
        : "حاسبة تسعير العمل الحر في السعودية 2026 — كم سعر خدمتك؟ | برولانسر";
      const d = document.querySelector('meta[name="description"]');
      if (d) d.setAttribute("content", isPricing
        ? "باقات اشتراك حاسبة برولانسر لتسعير خدمات العمل الحر — 3 اقتراحات AI مجانًا شهريًا، أو 30 اقتراحًا بـ 19 ر.س، أو اشتراك شهري بـ 99 ر.س لاستخدام لامحدود. دفع آمن عبر Tap Payments."
        : "كم أسعّر خدمتي كمستقل؟ حاسبة مجانية لتسعير العمل الحر في السعودية 2026 بالريال — سعر الساعة والمشروع حسب السوق، مع ضريبة القيمة المضافة 15% واقتراح ذكي بالذكاء الاصطناعي. تصميم شعار، متجر سلة وزد، مواقع، UI/UX، مونتاج، محتوى وسوشال ميديا.");
    } else {
      document.title = isPricing
        ? "Prolancer Calculator Subscription Plans — 30 suggestions 19 SAR · 99 SAR/mo Unlimited"
        : "Saudi Freelance Rate Calculator 2026 — How Much Should I Charge? | Prolancer";
      const d = document.querySelector('meta[name="description"]');
      if (d) d.setAttribute("content", isPricing
        ? "Prolancer freelance pricing calculator subscription plans — 3 free AI suggestions/month, 30 suggestions for 19 SAR, or 99 SAR/month unlimited. Secure one-time payment via Tap Payments."
        : "Free freelance rate calculator for Saudi Arabia 2026 — hourly and project prices in SAR from real market data, 15% VAT, and AI price suggestions. Logo, Salla/Zid stores, websites, UI/UX, video, content & social media.");
    }
  }, [lang, t.dir, initialAnchor]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    document.documentElement.classList.toggle("light", theme === "light");
    localStorage.setItem("pl_theme", theme);
  }, [theme]);

  // Deep-link scroll — supports /pricing route and ?anchor= / #hash
  useEffect(() => {
    const target = initialAnchor
      || (typeof window !== "undefined" && window.location.hash ? window.location.hash.replace("#", "") : null);
    if (!target) return;
    const timer = setTimeout(() => {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 400);
    return () => clearTimeout(timer);
  }, [initialAnchor]);

  // Open the account modal in admin mode when the URL has ?signin=admin
  // (used by the AdminDashboard redirect when the session key is missing).
  useEffect(() => {
    try {
      const p = new URLSearchParams(window.location.search);
      if (p.get("signin") === "admin") {
        setAccountMode("admin");
        setAccountOpen(true);
        // Clean the URL so the modal doesn't re-open on back/forward.
        const url = new URL(window.location.href);
        url.searchParams.delete("signin");
        window.history.replaceState({}, "", url.pathname + (url.search ? "?" + url.searchParams.toString() : "") + url.hash);
      }
    } catch {}
  }, []);

  // Keep the Calculator's `email` state (used for the old email-based credit
  // lookup) in sync with the authenticated user. This fixes subscription +
  // credits appearing in the header but not on /me, or vice-versa.
  useEffect(() => {
    if (!authUser?.email) return;
    const legacyEmail = (localStorage.getItem("pl_email") || "").toLowerCase();
    const authEmail = authUser.email.toLowerCase();
    // If the user has an existing legacy record under a different email,
    // transfer its credits/subscription into their authed account exactly once.
    if (legacyEmail && legacyEmail !== authEmail) {
      authAxios
        .post(`${API}/me/link-email`, { email: legacyEmail })
        .then((r) => { if (r.data?.linked) refresh?.(); })
        .catch(() => {});
    }
    setEmail(authUser.email);
  }, [authUser]);

  // On mount / when email changes, fetch remaining paid credits AND active subscription
  useEffect(() => {
    if (!email) { setPaidCredits(0); setSubscription(null); return; }
    localStorage.setItem("pl_email", email);
    axios.get(`${API}/user/credits`, { params: { email } })
      .then(r => {
        setPaidCredits(r.data.remaining || 0);
        setSubscription(r.data.subscription && r.data.subscription.active ? r.data.subscription : null);
      })
      .catch(() => {});
  }, [email]);

  // Persist currency
  useEffect(() => {
    localStorage.setItem("pl_currency", currency);
  }, [currency]);

  // Exit-intent → surface a popup once per session before the user leaves.
  // Audience-aware content:
  //   • Not claimed, no sub       → BonusFeedbackModal (gift)
  //   • Claimed, no sub           → ExitIntentModal variant="subscribe"
  //   • Monthly subscriber        → ExitIntentModal variant="yearly" (upgrade)
  //   • Yearly subscriber         → ExitIntentModal variant="share"  (community CTA)
  useEffect(() => {
    if (IS_EMBED) return;
    if (sessionStorage.getItem("pl_exit_intent_shown") === "1") return;

    const fire = (source) => {
      if (sessionStorage.getItem("pl_exit_intent_shown") === "1") return;
      const ae = document.activeElement;
      if (ae && (ae.tagName === "INPUT" || ae.tagName === "TEXTAREA" || ae.isContentEditable)) return;
      sessionStorage.setItem("pl_exit_intent_shown", "1");
      if (!bonusClaimed && !subscription) {
        setBonusOpen(true);
      } else if (subscription) {
        const plan = subscription.plan || "";
        setExitVariant(plan === "yearly_unlimited" || !yearlyOnRef.current ? "share" : "yearly");
        setExitOpen(true);
      } else {
        setExitVariant("subscribe");
        setExitOpen(true);
      }
      try { console.log("[prolancer] exit-intent fired via", source); } catch {}
    };

    const onMouseOut = (e) => {
      if (e.clientY <= 10 && !e.relatedTarget && !e.toElement) fire("mouseout-top");
    };

    let visTimer = null;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        visTimer = setTimeout(() => fire("visibility-hidden"), 4000);
      } else if (visTimer) {
        clearTimeout(visTimer);
        visTimer = null;
      }
    };

    const onPageHide = () => fire("pagehide");

    let scrollTimer = null;
    const onScroll = () => {
      const doc = document.documentElement;
      const scrolled = (window.scrollY + window.innerHeight) / Math.max(1, doc.scrollHeight);
      if (scrolled >= 0.88) {
        if (scrollTimer) return;
        scrollTimer = setTimeout(() => fire("scroll-bottom-idle"), 1500);
      } else if (scrollTimer) {
        clearTimeout(scrollTimer);
        scrollTimer = null;
      }
    };

    const attachTimer = setTimeout(() => {
      document.addEventListener("mouseout", onMouseOut);
      document.addEventListener("visibilitychange", onVisibility);
      window.addEventListener("pagehide", onPageHide);
      window.addEventListener("scroll", onScroll, { passive: true });
    }, 120000);

    return () => {
      clearTimeout(attachTimer);
      document.removeEventListener("mouseout", onMouseOut);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("scroll", onScroll);
      if (visTimer) clearTimeout(visTimer);
      if (scrollTimer) clearTimeout(scrollTimer);
    };
  }, [bonusClaimed, subscription]);

  // Embed beacon — when the widget runs inside an iframe on another website,
  // register the parent domain so Prolancer admins can audit third-party usage.
  const [embedBlocked, setEmbedBlocked] = useState(false);
  useEffect(() => {
    if (!IS_EMBED) return;
    try {
      let host = "";
      try {
        if (window.parent && window.parent !== window && document.referrer) {
          host = new URL(document.referrer).hostname;
        }
      } catch { host = ""; }
      const payload = {
        host,
        referrer: document.referrer || "",
        path: window.location.pathname + window.location.search,
        lang,
      };
      fetch(`${API}/embed/beacon`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
        keepalive: true,
      })
        .then((r) => r.json())
        .then((d) => { if (d && d.blocked) setEmbedBlocked(true); })
        .catch(() => {});
    } catch {}
  }, [lang]);

  const result = useMemo(
    () => computePricing({ service, experience, complexity, unit, quantity, revisions, rush, customRates }),
    [service, experience, complexity, unit, quantity, revisions, rush, customRates],
  );

  const refreshQuota = () => {
    axios.get(`${API}/pricing/ai-quota`, { params: { email: authUser?.email || email || undefined } })
      .then((r) => setAiQuota(r.data))
      .catch(() => {});
  };
  useEffect(() => {
    if (!IS_EMBED) refreshQuota();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authUser?.email, email]);

  const requestAI = async () => {
    if (!result || aiLoading) return;
    track("ai_request", null, { once: false });
    if (!aiReqIdRef.current) aiReqIdRef.current = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
    const t0 = performance.now();
    setAiLoading(true);
    setAiError(null);
    setAi(null);
    setPriceEdited(false);
    setRateBlocked(null);
    try {
      const { data } = await axios.post(`${API}/pricing/ai-suggest`, {
        request_id: aiReqIdRef.current,
        service,
        service_label: customServiceName.trim() || t.services[service],
        experience,
        complexity,
        unit,
        quantity: Number(quantity),
        revisions: Number(revisions),
        rush,
        calculated_avg: Math.round(result.avgPrice),
        calculated_min: Math.round(result.minPrice),
        calculated_max: Math.round(result.maxPrice),
        language: lang,
        project_description: description,
        // Prefer the authenticated user's email so JWT accounts' welcome
        // credits + paid credits get consumed instead of hitting the IP quota.
        email: authUser?.email || email || undefined,
      });
      setAi(data);
      track("ai_success", { latency_ms: Math.round(performance.now() - t0), code: data._replayed ? "replayed" : data?._rate_limit?.cache_hit ? "cache" : "live" }, { once: false });
      setFinalPrice(Math.round(data.recommended_price || 0));
      if (data._rate_limit) {
        setRateInfo(data._rate_limit);
        if (data._rate_limit.credit_bypass) setPaidCredits(data._rate_limit.credits_remaining || 0);
        if (data._rate_limit.subscription_bypass) {
          setSubscription({
            plan: data._rate_limit.subscription_plan,
            active: true,
            expires_at: data._rate_limit.subscription_expires_at,
          });
        }
      }
      // Auto-save every generated AI suggestion to the signed-in user's
      // profile — silent, no button, no confirmation. 30-day TTL on the
      // collection keeps the DB small.
      if (authUser) {
        const title = (description?.trim() || t.services?.[service] || "عرض سعر").slice(0, 80);
        authAxios.post(`${API}/me/proposals`, {
          title,
          service: t.services?.[service] || service,
          inputs: {
            service, experience, complexity, unit,
            quantity: Number(quantity), revisions: Number(revisions),
            rush, currency, projectDescription: description,
            calculated_min: Math.round(result.minPrice),
            calculated_avg: Math.round(result.avgPrice),
            calculated_max: Math.round(result.maxPrice),
          },
          result: {
            finalPrice: Math.round(data.recommended_price || 0),
            recommended_price: data.recommended_price,
            win_rate_price: data.win_rate_price,
            price_options: data.price_options,
            confidence: data.confidence,
            reasoning: data.reasoning,
            tips: data.tips,
          },
        }).catch(() => {}); // silent — never block UX on persistence
      }
      // Nudge the user to leave feedback — delay 15s so they have time to
      // read the result first.
      if (!data?._rate_limit?.subscription_bypass) {
        setTimeout(() => setBonusOpen(true), 15000);
      }
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = (detail && typeof detail === "object" && detail.code) || (status ? `http_${status}` : "network");
      track("ai_fail", { code, latency_ms: Math.round(performance.now() - t0) }, { once: false });
      if (status === 429 && detail && typeof detail === "object") {
        setRateBlocked(detail);
        setRateInfo({ used: detail.limit, remaining: 0, limit: detail.limit });
        toast.error(lang === "ar" ? detail.message_ar : detail.message_en);
      } else {
        const msg = detail && typeof detail === "object"
          ? (lang === "ar" ? detail.message_ar : detail.message_en)
          : (lang === "ar" ? "تعذّر الاتصال بخدمة الذكاء الاصطناعي. لم يُخصم أي رصيد — حاول مرة أخرى." : "Couldn't reach the AI service. Nothing was deducted — please try again.");
        setAiError(msg);
      }
    } finally {
      setAiLoading(false);
      refreshQuota();
    }
  };

  const applyAI = () => {
    if (!ai?.recommended_price || !result) return;
    // Adjust quantity so that avg matches recommendation
    const targetHourly = ai.recommended_price / (result.hours * result.expM * result.cxM * (rush ? 1.3 : 1) * (1 + 0.05 * Math.max(0, revisions - 2)));
    // Save as a custom rate override for this service to reflect user choice
    const nextRates = { ...customRates, [service]: { ...customRates[service], avg: Math.round(targetHourly) } };
    setCustomRates(nextRates);
    localStorage.setItem("pl_rates", JSON.stringify(nextRates));
    toast.success(lang === "ar" ? "تم تطبيق السعر الذكي" : "AI price applied");
  };

  const resetRates = () => setEditingRates(DEFAULT_RATES);
  const saveRates = () => {
    setCustomRates(editingRates);
    localStorage.setItem("pl_rates", JSON.stringify(editingRates));
    setRatesOpen(false);
    toast.success(lang === "ar" ? "تم حفظ الأسعار" : "Rates saved");
  };

  const dark = theme === "dark";

  return (
    <div className={`min-h-screen ${dark ? "dark" : ""}`} dir={t.dir}>
      <Toaster position={t.dir === "rtl" ? "top-left" : "top-right"} theme={theme} richColors />

      {/* Blocked embed — admin has revoked this domain's permission to host the widget. */}
      {IS_EMBED && embedBlocked && (
        <div
          data-testid="embed-blocked"
          dir={lang === "ar" ? "rtl" : "ltr"}
          className="fixed inset-0 z-[200] flex items-center justify-center p-6 bg-[#182138] text-white"
        >
          <div className="max-w-md w-full rounded-3xl border border-red-500/40 bg-[#1f2a47] p-8 text-center">
            <div className="inline-flex items-center justify-center h-14 w-14 rounded-full bg-red-500/15 text-red-400 mb-4">
              <X className="h-7 w-7" />
            </div>
            <h2 className={`text-xl font-extrabold ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              {lang === "ar" ? "تم إيقاف هذا التضمين" : "This embed has been disabled"}
            </h2>
            <p className={`mt-3 text-sm text-white/70 leading-relaxed ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              {lang === "ar"
                ? "تم إيقاف استخدام حاسبة برولانسر على هذا الموقع من قِبل إدارة برولانسر. لاستخدامها رسميًا، يرجى زيارة الموقع الأصلي."
                : "Prolancer has disabled the embed on this website. Please visit the original site to use the calculator."}
            </p>
            <a
              href="https://prolancer.app"
              target="_blank"
              rel="noopener noreferrer"
              className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold bg-[#18c98f] text-white hover:brightness-110"
            >
              {lang === "ar" ? "افتح على prolancer.app" : "Open on prolancer.app"}
            </a>
          </div>
        </div>
      )}

      {/* Embed-only brand protection: diagonal watermark + fixed "Powered by" badge.
          Both are pointer-events-none and sit on top of content so hosts can't easily hide them. */}
      {IS_EMBED && (
        <>
          <div
            data-testid="embed-watermark"
            aria-hidden="true"
            className="pointer-events-none fixed inset-0 z-[60] overflow-hidden select-none"
            style={{
              backgroundImage:
                "repeating-linear-gradient(-30deg, transparent 0, transparent 180px, rgba(24,201,143,0.06) 180px, rgba(24,201,143,0.06) 181px)",
            }}
          >
            <div
              className="absolute inset-0 flex items-center justify-center opacity-[0.07]"
              style={{ transform: "rotate(-24deg)" }}
            >
              <div className="whitespace-nowrap text-[14vw] font-extrabold tracking-tighter text-[#18c98f]">
                PROLANCER · برولانسر
              </div>
            </div>
          </div>

          <a
            data-testid="embed-powered-by"
            href="https://prolancer.app"
            target="_blank"
            rel="noopener noreferrer"
            dir={lang === "ar" ? "rtl" : "ltr"}
            className={`fixed z-[70] ${lang === "ar" ? "left-3 sm:left-4" : "right-3 sm:right-4"} bottom-3 sm:bottom-4 group inline-flex items-center gap-2 px-3.5 py-2 rounded-full border backdrop-blur-xl text-[11px] sm:text-xs font-extrabold shadow-[0_12px_32px_-12px_rgba(24,201,143,0.55)] transition-all hover:-translate-y-0.5 ${
              dark
                ? "bg-[#182138]/85 border-[#18c98f]/40 text-white"
                : "bg-white/90 border-[#18c98f]/50 text-[#182138]"
            } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
          >
            <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-[#18c98f] text-white text-[10px] font-black shadow-[0_0_14px_rgba(24,201,143,0.65)]">
              P
            </span>
            <span className="opacity-80">
              {lang === "ar" ? "مُشغّل بواسطة" : "Powered by"}
            </span>
            <span className="text-[#18c98f] group-hover:underline">Prolancer</span>
            <span className="hidden sm:inline opacity-50">· prolancer.app</span>
          </a>
        </>
      )}

      {/* Header */}
      {!IS_EMBED && (
      <header
        data-testid="site-header"
        className={`sticky top-0 z-40 backdrop-blur-xl border-b ${
          dark ? "bg-[#1f2a47]/85 border-[#2e3a5c]" : "bg-[#f3f8ff]/90 border-[#e5e5ea]"
        } no-print`}
        style={{ paddingTop: "env(safe-area-inset-top)" }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between gap-3">
          <div data-testid={CALC.brand} className="flex items-center gap-3">
            <LogoLockup lang={lang} theme={theme} />
            <div className={`hidden md:flex flex-col items-start ps-3 border-s ${dark ? "border-[#2e3a5c]" : "border-[#e5e5ea]"}`}>
              <span
                data-testid="app-tagline"
                className={`text-xs sm:text-sm font-bold ${lang === "ar" ? "font-arabic" : "font-outfit"} ${dark ? "text-white" : "text-[#2d2c35]"}`}
              >
                {t.tagline}
              </span>
              <span className={`text-[10px] font-semibold tracking-[0.3em] mt-0.5 text-[#18c98f]`}>SAR ﷼ · 2026</span>
            </div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            {/* Account chip */}
            <Button
              data-testid="account-open-button"
              variant="ghost"
              size="sm"
              onClick={() => {
                if (authUser) {
                  navigate("/me");
                } else {
                  setAuthModalMode("login");
                  setAuthModalOpen(true);
                }
              }}
              className={`gap-2 ${dark ? "text-white hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
            >
              <div className="relative">
                {authUser?.picture ? (
                  <img src={authUser.picture} alt="" className="h-5 w-5 rounded-full object-cover" />
                ) : (
                  <User className="h-4 w-4" />
                )}
                {/* Green dot = signed in. Hidden for anonymous visitors even if they have paid credits. */}
                {authUser && (
                  <span className="absolute -top-1 -end-1 h-2 w-2 rounded-full bg-[#18c98f] ring-2 ring-[#1f2a47]" />
                )}
              </div>
              <span className="hidden md:inline max-w-[140px] truncate text-xs" dir="ltr">
                {authUser
                  ? (authUser.name || authUser.email.split("@")[0])
                  : email
                  ? `${email.split("@")[0]} · ${subscription ? "∞" : paidCredits}`
                  : (lang === "ar" ? "تسجيل" : "Sign in")}
              </span>
            </Button>

            {/* Always-visible Buy Credits button */}
            <Button
              data-testid="header-buy-button"
              size="sm"
              onClick={() => openBuy("header")}
              className="gap-1.5 bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold rounded-full px-3 h-8"
            >
              <ShoppingCart className="h-3.5 w-3.5" />
              <span className="text-xs">{t.ai.buyMore}</span>
            </Button>

            {/* Currency dropdown removed from header — each calculator now has its own in-context switcher that stays in sync. */}

            <Button
              data-testid="nav-pricing"
              variant="ghost"
              size="sm"
              onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" })}
              className={`hidden sm:inline-flex gap-2 ${dark ? "text-[#18c98f] hover:bg-[#2e3a5c]" : "text-[#12a777] hover:bg-[#f3f8ff]"}`}
            >
              <Crown className="h-4 w-4" />
              <span className="hidden lg:inline">{lang === "ar" ? "اشترك" : "Subscribe"}</span>
            </Button>
            <Button
              data-testid="integrate-open-button"
              variant="ghost"
              size="sm"
              onClick={() => setIntegrateOpen(true)}
              className={`hidden sm:inline-flex gap-2 ${dark ? "text-white hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
            >
              <Share2 className="h-4 w-4" />
              <span className="hidden lg:inline">{t.integrate.button}</span>
            </Button>
            <Button
              data-testid={CALC.ratesOpenBtn}
              variant="ghost"
              size="sm"
              onClick={() => { setEditingRates(customRates); setRatesOpen(true); }}
              className={`hidden sm:inline-flex gap-2 ${dark ? "text-white hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
            >
              <Settings2 className="h-4 w-4" />
              <span className="hidden lg:inline">{t.nav.rates}</span>
            </Button>
            <Button
              data-testid={CALC.langToggle}
              variant="ghost"
              size="sm"
              onClick={() => setLang(lang === "ar" ? "en" : "ar")}
              className={`hidden sm:inline-flex gap-2 ${dark ? "text-white hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
            >
              <Languages className="h-4 w-4" />
              <span className="font-bold">{t.langToggle}</span>
            </Button>
            <Button
              data-testid={CALC.themeToggle}
              variant="ghost"
              size="icon"
              onClick={() => setTheme(dark ? "light" : "dark")}
              className={`hidden sm:inline-flex ${dark ? "text-[#18c98f] hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
              aria-label="theme"
            >
              {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            {/* Mobile overflow menu — groups Share / Rates / Lang / Theme */}
            <DropdownMenu modal={false}>
              <DropdownMenuTrigger asChild>
                <Button
                  data-testid="mobile-overflow-button"
                  variant="ghost"
                  size="icon"
                  aria-label="menu"
                  className={`sm:hidden h-11 w-11 ${dark ? "text-white hover:bg-[#2e3a5c]" : "text-[#2d2c35] hover:bg-[#f3f8ff]"}`}
                >
                  <MoreVertical className="h-5 w-5" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                collisionPadding={12}
                className={`min-w-[12rem] ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white"}`}
                dir={lang === "ar" ? "rtl" : "ltr"}
              >
                <DropdownMenuItem
                  data-testid="mobile-menu-pricing"
                  onClick={() => document.getElementById("pricing")?.scrollIntoView({ behavior: "smooth", block: "start" })}
                  className={`gap-2 cursor-pointer ${dark ? "focus:bg-[#2e3a5c]" : ""}`}
                >
                  <Crown className="h-4 w-4 text-[#18c98f]" />
                  <span>{lang === "ar" ? "اشترك" : "Subscribe"}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-integrate"
                  onClick={() => setIntegrateOpen(true)}
                  className={`gap-2 cursor-pointer ${dark ? "focus:bg-[#2e3a5c]" : ""}`}
                >
                  <Share2 className="h-4 w-4 text-[#18c98f]" />
                  <span>{t.integrate.button}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-rates"
                  onClick={() => { setEditingRates(customRates); setRatesOpen(true); }}
                  className={`gap-2 cursor-pointer ${dark ? "focus:bg-[#2e3a5c]" : ""}`}
                >
                  <Settings2 className="h-4 w-4 text-[#18c98f]" />
                  <span>{t.nav.rates}</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator className={dark ? "bg-[#2e3a5c]" : ""} />
                <DropdownMenuItem
                  data-testid="mobile-menu-lang"
                  onClick={() => setLang(lang === "ar" ? "en" : "ar")}
                  className={`gap-2 cursor-pointer ${dark ? "focus:bg-[#2e3a5c]" : ""}`}
                >
                  <Languages className="h-4 w-4 text-[#18c98f]" />
                  <span>{lang === "ar" ? "English" : "العربية"}</span>
                </DropdownMenuItem>
                <DropdownMenuItem
                  data-testid="mobile-menu-theme"
                  onClick={() => setTheme(dark ? "light" : "dark")}
                  className={`gap-2 cursor-pointer ${dark ? "focus:bg-[#2e3a5c]" : ""}`}
                >
                  {dark
                    ? <Sun className="h-4 w-4 text-[#18c98f]" />
                    : <Moon className="h-4 w-4 text-[#18c98f]" />}
                  <span>{dark ? t.themeToggleDark : t.themeToggleLight}</span>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </header>
      )}

      {/* Hero */}
      {!IS_EMBED && (
      <>
      <ToolsNav dark={dark} lang={lang} t={t} />

      <div data-testid="pricing-tool-frame" className="p-tool-frame mx-3 sm:mx-5 lg:mx-8 mt-6 sm:mt-8">
      <section id="pricing-tool" data-testid="pricing-tool-section" className="p-hero-bg relative overflow-hidden scroll-mt-28">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-10">
          <div data-testid="pricing-tool-chip" className={`p-tool-chip ${lang === "ar" ? "is-ar font-arabic" : "font-outfit"}`}>
            {lang === "ar" ? "حاسبة التسعير" : "Pricing Calculator"}
          </div>
        </div>
        <div className="p-grain absolute inset-0" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 sm:pt-12 pb-8 sm:pb-12">
          <Badge className={`mb-4 ${dark ? "bg-[#18c98f]/15 text-[#18c98f] hover:bg-[#18c98f]/20" : "bg-[#18c98f]/15 text-[#12a777] hover:bg-[#18c98f]/25"} border-0 rounded-full px-3 py-1 text-xs`}>
            <span className="mr-1.5 h-1.5 w-1.5 rounded-full bg-[#18c98f] inline-block animate-pulse" />
            {t.hero.badge}
          </Badge>
          <h1 className={`${lang === "ar" ? "font-arabic" : "font-outfit"} font-bold tracking-tight text-[2rem] leading-tight sm:text-5xl lg:text-6xl max-w-4xl break-words ${dark ? "text-white" : "text-[#000000]"}`}>
            {(() => {
              const words = t.hero.title.split(" ");
              const last = words.pop();
              return <>{words.join(" ")} <span data-testid="hero-title-accent" className="text-[#18c98f]">{last}</span></>;
            })()}
          </h1>
          <p className={`${lang === "ar" ? "font-arabic" : "font-outfit"} mt-3 max-w-2xl text-sm sm:text-lg leading-relaxed break-words ${dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
            {t.hero.subtitle}
          </p>

          <HeroIntro lang={lang} dark={dark} onStart={startPricing} />

          {/* Embedded live platform stats — merged into the hero per user request
              so "منصة نشطة" shares the same frame/width as the hero. */}
          <div className="mt-5">
            <VisitorCounter lang={lang} theme={theme} embed />
          </div>
          {/* Freemium pill, currency switcher, and install button moved out of hero:
              • Freemium info lives inside the AI pricing card (`pricing-hint`).
              • Currency switcher is now inside each calculator form.
              • Install button lives in the sticky top tool nav.
              Keeping the hero focused only on the headline + subtitle makes the first screen cleaner. */}
          <div data-testid="hero-crown" className="mt-5">
            {subscription && (
              <div className={`inline-flex w-fit max-w-full flex-wrap items-center gap-x-2 gap-y-1.5 px-3 sm:px-4 py-2 rounded-2xl sm:rounded-full border ${dark ? "bg-[#18c98f]/10 border-[#18c98f]/40 text-white" : "bg-[#18c98f]/10 border-[#18c98f]/40 text-[#2d2c35]"}`}>
                <Crown className="h-4 w-4 text-[#18c98f] shrink-0" />
                <span className={`text-xs sm:text-sm font-bold text-[#18c98f] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                  {subscription.plan === "yearly_unlimited"
                    ? (lang === "ar" ? "اشتراكك السنوي نشط · اقتراحات أسعار لامحدودة ∞" : "Yearly subscription active · Unlimited ∞")
                    : (lang === "ar" ? "اشتراكك الشهري نشط · اقتراحات أسعار لامحدودة ∞" : "Monthly subscription active · Unlimited ∞")}
                </span>
              </div>
            )}
          </div>
        </div>
      </section>
      </div>
      </>
      )}

      {/* Calculator grid */}
      <section id="pricing-grid" data-testid="pricing-grid-frame" className={`p-scroll-anchor ${!IS_EMBED ? "p-tool-frame mx-3 sm:mx-5 lg:mx-8 mt-4 sm:mt-5" : ""} ${dark ? "bg-[#1f2a47]" : "bg-[#f3f8ff]"}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-3 lg:pt-5 grid grid-cols-1 lg:grid-cols-12 gap-5 lg:gap-6">
          {/* LEFT — form (desktop) / BELOW the display (mobile) */}
          <div className="lg:col-span-7 space-y-4 order-2 lg:order-1">
            <CalcStepper lang={lang} dark={dark} started={inputsKey !== initialInputsKey.current} done={!!ai} />
            {/* Services */}
            <FormBlock title={t.form.serviceHeading} dark={dark} lang={lang}>
              <div className="grid grid-cols-4 gap-1.5 sm:gap-2">
                {SERVICE_LIST.map((s) => {
                  const Icon = ICONS[s.icon];
                  const active = service === s.id;
                  return (
                    <button
                      key={s.id}
                      data-testid={CALC.serviceCard(s.id)}
                      onClick={() => setService(s.id)}
                      className={`p-service-card group p-1 sm:p-1.5 rounded-lg text-center border min-h-[3.75rem] sm:h-14 flex flex-col sm:flex-row items-center justify-center gap-1 sm:gap-1.5 transition-all ${
                        active
                          ? dark
                            ? "bg-[#18c98f]/15 border-[#18c98f] shadow-[0_0_0_1px_rgba(24,201,143,0.4),0_4px_12px_-6px_rgba(24,201,143,0.5)]"
                            : "bg-white border-[#18c98f] shadow-[0_0_0_1px_rgba(24,201,143,0.4),0_4px_12px_-6px_rgba(24,201,143,0.5)]"
                          : dark
                          ? "bg-[#1f2a47] border-[#2e3a5c] hover:border-[#18c98f]/60"
                          : "bg-white border-[#e5e5ea] hover:border-[#18c98f]/60"
                      }`}
                    >
                      {/* Icon with colored background badge — ultra compact inline */}
                      <span
                        className={`inline-flex items-center justify-center h-5 w-5 rounded-md transition-all shrink-0 ${
                          active
                            ? "bg-[#18c98f]"
                            : dark
                            ? "bg-[#18c98f]/15 group-hover:bg-[#18c98f]/25"
                            : "bg-[#18c98f]/10 group-hover:bg-[#18c98f]/20"
                        }`}
                      >
                        <Icon
                          className={`h-3 w-3 transition-colors ${
                            active ? "text-[#182138]" : "text-[#18c98f]"
                          }`}
                          strokeWidth={2.5}
                        />
                      </span>
                      <span
                        className={`text-[10px] sm:text-xs font-bold leading-[1.15] line-clamp-2 [overflow-wrap:normal] [word-break:keep-all] ${lang === "ar" ? "font-arabic" : "font-outfit"} ${
                          active
                            ? dark
                              ? "text-white"
                              : "text-[#000000]"
                            : dark
                            ? "text-white/90"
                            : "text-[#000000]"
                        }`}
                      >
                        {t.services[s.id]}
                      </span>
                    </button>
                  );
                })}
              </div>
              <OtherServiceField lang={lang} dark={dark} open={otherOpen} setOpen={setOtherOpen} value={customServiceName} setValue={setCustomServiceName} categoryLabel={t.services[service]} />
            </FormBlock>

            {/* Experience & Complexity — stacked labels + full-width pill grids */}
            <div className={`p-card p-3 sm:p-4 space-y-2.5 p-editable-hint`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className={`text-[11px] font-bold shrink-0 flex items-center gap-1.5 text-[#f97316] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#f97316] animate-pulse" />
                  {t.form.experienceHeading}
                </h3>
                <div className="grid grid-cols-3 gap-1 flex-1 max-w-[260px]">
                  {["beginner", "intermediate", "expert"].map((k) => (
                    <PillButton key={k} active={experience === k} onClick={() => setExperience(k)} testId={CALC.expPill(k)} dark={dark}>
                      {t.experience[k]}
                    </PillButton>
                  ))}
                </div>
              </div>
              <div className={`h-px ${dark ? "bg-[#2e3a5c]" : "bg-[#e5e5ea]"}`} />
              <div className="flex items-center justify-between gap-2">
                <h3 className={`text-[11px] font-bold shrink-0 flex items-center gap-1.5 text-[#f97316] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#f97316] animate-pulse" />
                  {t.form.complexityHeading}
                </h3>
                <div className="grid grid-cols-3 gap-1 flex-1 max-w-[260px]">
                  {["simple", "medium", "complex"].map((k) => (
                    <PillButton key={k} active={complexity === k} onClick={() => setComplexity(k)} testId={CALC.cxPill(k)} dark={dark}>
                      {t.complexity[k]}
                    </PillButton>
                  ))}
                </div>
              </div>
            </div>

            {/* Scope — ultra-compact single card */}
            <div className={`p-card p-2.5 sm:p-3 space-y-2 p-editable-hint`}>
              <div className="flex items-center justify-between gap-2">
                <h3 className={`text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 text-[#f97316] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                  <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#f97316] animate-pulse" />
                  {t.form.scopeHeading}
                </h3>
                {/* Rush — compact inline pill with switch on the right of the heading */}
                <label htmlFor="rush" className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-full border cursor-pointer text-[11px] font-bold ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-[#18c98f]" : "bg-white border-[#e5e5ea] text-[#12a777]"}`}>
                  <Zap className="h-3 w-3" />
                  {t.form.rush}
                  <Switch id="rush" data-testid={CALC.rushToggle} checked={rush} onCheckedChange={setRush} className="scale-75 -mx-1" />
                </label>
              </div>
              <div className="grid grid-cols-3 gap-1.5">
                <div>
                  <Label className={`block text-[9px] mb-0.5 ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`}>{t.form.unit}</Label>
                  <Select value={unit} onValueChange={setUnit}>
                    <SelectTrigger data-testid={CALC.unitSelect} className={`h-8 text-[11px] px-2 ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white border-[#e5e5ea]"}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className={dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : ""}>
                      <SelectItem value="hour">{t.form.unitHour}</SelectItem>
                      <SelectItem value="day">{t.form.unitDay}</SelectItem>
                      <SelectItem value="project">{t.form.unitProject}</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className={`block text-[9px] mb-0.5 ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`}>{t.form.quantity}</Label>
                  <Input
                    data-testid={CALC.quantityInput}
                    type="text"
                    inputMode="decimal"
                    value={digits === "ar" ? localizeDigits(String(quantity), "ar") : quantity}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const v = normalizeDigits(e.target.value);
                      setQuantity(v === "" ? 0 : parseFloat(v) || 0);
                    }}
                    className={`h-8 text-[11px] text-center px-1 ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white font-mono-num" : "bg-white border-[#e5e5ea] font-mono-num"} ${!(Number(quantity) > 0) ? "border-[#f97316]" : ""}`}
                  />
                  {!(Number(quantity) > 0) && (
                    <p data-testid="quantity-error" role="alert" className="mt-0.5 text-[9.5px] font-bold text-[#f97316]">
                      {lang === "ar" ? "أدخل كمية أكبر من صفر" : "Enter a quantity above zero"}
                    </p>
                  )}
                </div>
                <div>
                  <Label className={`block text-[9px] mb-0.5 ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`}>{t.form.revisions}</Label>
                  <Input
                    data-testid={CALC.revisionsInput}
                    type="text"
                    inputMode="numeric"
                    value={digits === "ar" ? localizeDigits(String(revisions), "ar") : revisions}
                    onFocus={(e) => e.target.select()}
                    onChange={(e) => {
                      const v = normalizeDigits(e.target.value);
                      setRevisions(v === "" ? 0 : parseInt(v, 10) || 0);
                    }}
                    className={`h-8 text-[11px] text-center px-1 ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white font-mono-num" : "bg-white border-[#e5e5ea] font-mono-num"}`}
                  />
                </div>
              </div>
              <Textarea
                data-testid={CALC.descriptionInput}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t.form.descriptionPh}
                rows={1}
                className={`text-[11px] min-h-[32px] py-1.5 px-2 resize-none ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white placeholder:text-white/35" : "bg-white border-[#e5e5ea]"}`}
              />
              <UnitHint lang={lang} unit={unit} dark={dark} />
              <ScopeDetails lang={lang} dark={dark} service={service} value={scopeDetails[service] || {}} onChange={(v) => setScopeDetails({ ...scopeDetails, [service]: v })} />
            </div>

            <AiPanel
              lang={lang}
              dark={dark}
              quota={aiQuota}
              loading={aiLoading}
              error={aiError}
              onRequest={requestAI}
              onBuy={() => openBuy("ai_panel")}
              disabled={!(Number(quantity) > 0) || aiQuota?.next_cost === "blocked" || rateBlocked?.code === "sub_daily_cap"}
            />
          </div>

          {/* RIGHT — AI-driven pricing display (shown FIRST on mobile — like a calculator LCD) */}
          <div className="lg:col-span-5 order-1 lg:order-2">
            <div data-testid={CALC.aiCard} className={`p-card p-4 sm:p-6 lg:sticky lg:top-24 ${ai ? "p-gold-ring" : "p-glow-border"}`}>
              {/* Compact header — just the title (currency moved below next to the buy strip) */}
              <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.2em] font-extrabold ${dark ? "text-[#18c98f]" : "text-[#12a777]"}`}>
                <Sparkles className="h-3.5 w-3.5" />
                {t.ai.title}
              </div>

              {/* LCD-style rectangular result display — authentic Casio-style light olive LCD with crisp dark amber readout + green frame */}
              <div
                ref={pricingLcdRef}
                data-testid="pricing-lcd"
                className="relative mt-2"
                style={{
                  padding: "2.5px",
                  borderRadius: "14px",
                  border: "2px solid #18c98f",
                  background: "linear-gradient(180deg, #b5b89d 0%, #c8cbb0 50%, #a8ab90 100%)",
                  boxShadow: dark
                    ? "0 0 22px rgba(24,201,143,0.45), 0 12px 28px -14px rgba(0,0,0,0.55), 0 2px 0 0 rgba(0,0,0,0.3) inset"
                    : "0 0 18px rgba(24,201,143,0.35), 0 12px 28px -14px rgba(0,0,0,0.25), 0 2px 0 0 rgba(0,0,0,0.08) inset",
                }}
              >
                <div
                  className="relative overflow-hidden"
                  style={{
                    borderRadius: "11px",
                    background: "linear-gradient(180deg, #c2c5aa 0%, #b2b59a 100%)",
                    boxShadow: "inset 0 0 40px rgba(0,0,0,0.12), inset 0 2px 0 rgba(0,0,0,0.08)",
                  }}
                >
                  {/* LCD horizontal lines texture */}
                  <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.1] pointer-events-none"
                    style={{
                      backgroundImage:
                        "repeating-linear-gradient(0deg, transparent 0, transparent 3px, currentColor 3px, currentColor 4px)",
                      color: "#1a1a0d",
                    }}
                  />
                  <div className="relative px-4 py-3 sm:px-5 sm:py-4 text-center min-h-[96px] flex flex-col items-center justify-center">
                    {/* Service · experience label — inside the LCD screen */}
                    <div
                      className="text-sm sm:text-base font-black tracking-wide truncate max-w-full"
                      style={{ color: "#4a2b00" }}
                    >
                      {(customServiceName.trim() || t.services[service])} · {t.experience[experience]}
                    </div>
                    {aiLoading ? (
                      <>
                        <RefreshCw className="h-5 w-5 mt-1.5 mb-1 animate-spin" style={{ color: "#7c2d12" }} />
                        <div className="text-[11px] font-black" style={{ color: "#4a2b00" }}>{t.ai.loading}</div>
                      </>
                    ) : ai ? (
                      <>
                        <div data-testid="lcd-mode-ai" className="mt-1 text-[9px] uppercase tracking-[0.3em] font-black" style={{ color: "#4a2b00" }}>
                          {priceEdited ? t.ai.finalPrice : t.ai.recommended}
                        </div>
                        <div
                          className="mt-0.5 lcd-digits font-black text-5xl sm:text-6xl leading-none"
                          dir="ltr"
                          style={{
                            color: "#7c2d12",
                            textShadow: "0 1px 0 rgba(255,255,255,0.15), 0 0 2px rgba(0,0,0,0.3)",
                          }}
                        >
                          {formatMoney(finalPrice || 0, currency, lang, digits).value}
                        </div>
                        <div className="mt-1 text-[11px] font-black" dir="ltr" style={{ color: "#4a2b00" }}>
                          {formatMoney(finalPrice || 0, currency, lang, digits).symbol}
                        </div>
                      </>
                    ) : (
                      <>
                        <div
                          className="mt-1 lcd-digits font-black text-5xl sm:text-6xl leading-none"
                          dir="ltr"
                          style={{
                            color: "#7c2d12",
                            opacity: 0.5,
                            letterSpacing: "0.12em",
                          }}
                        >
                          — — —
                        </div>
                        <div className="mt-1.5 text-[10px] font-black" style={{ color: "#4a2b00" }}>
                          {lang === "ar" ? "اضغط «احسب الآن» للحصول على السعر" : "Tap “Calculate now” to get your price"}
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>

              {/* Compact action strip — Currency selector + free-counter hint + Buy, all in ONE row */}
              <div
                data-testid="pricing-hint"
                className={`mt-2.5 rounded-xl px-2.5 py-1.5 border text-[11px] flex items-center gap-2 ${dark ? "bg-[#18c98f]/8 border-[#18c98f]/30 text-white/85" : "bg-[#18c98f]/8 border-[#18c98f]/30 text-[#2d2c35]"}`}
              >
                <CurrencySwitcher
                  lang={lang}
                  theme={theme}
                  currency={currency}
                  setCurrency={setCurrency}
                  testIdPrefix="pricing-currency"
                  size="sm"
                />
                {!subscription && (
                  <>
                    <Zap className="h-3.5 w-3.5 text-[#18c98f] shrink-0" />
                    <span data-testid="pricing-quota-hint" className="flex-1 leading-tight">{quotaHint()}</span>
                    <button
                      data-testid="pricing-hint-buy"
                      onClick={() => openBuy("lcd_strip")}
                      className="font-bold text-[#18c98f] hover:underline shrink-0"
                    >
                      {t.ai.buyMore} →
                    </button>
                  </>
                )}
              </div>

              {ai && !aiLoading && (
                <div className="mt-3 space-y-1.5">
                  <Button
                    data-testid="export-pdf-button"
                    onClick={() => setProposalOpen(true)}
                    className="w-full h-11 bg-[#1f2a47] border-2 border-[#18c98f] text-white hover:bg-[#18c98f] hover:text-[#182138] font-black rounded-[14px] gap-2 transition-colors"
                  >
                    <FileDown className="h-4 w-4" />
                    {lang === "ar" ? "جهّز عرض سعر PDF باسمك — مجانًا" : "Prepare your PDF proposal — free"}
                  </Button>
                  <p className={`text-center text-[10.5px] ${dark ? "text-white/55" : "text-[#2d2c35]/60"}`}>
                    {lang === "ar"
                      ? "يُستخدم السعر المقترح، ويمكنك تعديله في شاشة المراجعة."
                      : "Uses the suggested price — editable on the review screen."}
                  </p>
                </div>
              )}

              {/* Primary CTA removed — the main "احسب الآن" button now lives
                  below the inputs, so this duplicate above the LCD is dropped
                  to avoid redundancy. */}

              {/* Secondary action — clears the AI result and scrolls the user back
                  to the service picker so they can start a fresh estimate with
                  new inputs. Only visible after an AI suggestion is shown. */}
              {ai && !aiLoading && (
                <button
                  data-testid="pricing-new-calc"
                  onClick={() => {
                    setAi(null);
                    setPriceEdited(false);
                    setFinalPrice(null);
                    document.querySelector("#pricing-grid")?.scrollIntoView({ behavior: "smooth", block: "start" });
                  }}
                  className={`mt-3 w-full h-10 rounded-full inline-flex items-center justify-center gap-2 text-xs font-bold border transition ${
                    dark
                      ? "border-[#18c98f]/40 bg-[#18c98f]/8 text-[#18c98f] hover:bg-[#18c98f]/15"
                      : "border-[#18c98f]/40 bg-[#18c98f]/8 text-[#12a777] hover:bg-[#18c98f]/15"
                  } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                  {lang === "ar" ? "حساب جديد بمدخلات مختلفة" : "New calculation with different inputs"}
                </button>
              )}

              <AnimatePresence>
                {ai && !aiLoading && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="mt-5 space-y-5">
                    {/* Confidence + reset — compact header for the detail section */}
                    <div className="flex items-center justify-between gap-2 text-[11px]">
                      <div className={`${dark ? "text-white/55" : "text-[#2d2c35]/60"}`}>
                        {t.ai.confidence}: <span className="font-semibold">{ai.confidence || 0}%</span>
                      </div>
                      {priceEdited && (
                        <button
                          onClick={() => { setFinalPrice(Math.round(ai.recommended_price)); setPriceEdited(false); }}
                          className={`font-semibold ${dark ? "text-[#18c98f]" : "text-[#12a777]"} hover:underline`}
                        >
                          <RefreshCw className="h-3 w-3 inline me-1" />{t.ai.reset}
                        </button>
                      )}
                    </div>

                    {/* Price options tiers */}
                    {Array.isArray(ai.price_options) && ai.price_options.length > 0 && (
                      <div>
                        <div className={`text-[11px] uppercase tracking-widest mb-2 ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                          {t.ai.options}
                        </div>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                          {ai.price_options.map((opt, i) => {
                            const active = finalPrice === Math.round(opt.price);
                            return (
                              <button
                                key={i}
                                data-testid={`price-option-${i}`}
                                onClick={() => { setFinalPrice(Math.round(opt.price)); setPriceEdited(true); }}
                                className={`rounded-xl p-3 border text-start transition flex flex-row sm:flex-col items-center sm:items-start justify-between sm:justify-start gap-3 sm:gap-0 min-h-[56px] sm:min-h-0 ${
                                  active
                                    ? "bg-[#18c98f]/15 border-[#18c98f]"
                                    : dark
                                    ? "bg-[#1f2a47]/60 border-[#2e3a5c] hover:border-[#18c98f]/60"
                                    : "bg-white border-[#e5e5ea] hover:border-[#18c98f]/60"
                                }`}
                              >
                                <div className="min-w-0 flex-1">
                                  <div className={`text-[10px] font-bold uppercase tracking-wider ${active ? "text-[#18c98f]" : dark ? "text-white/70" : "text-[#2d2c35]/70"}`}>
                                    {opt.label}
                                  </div>
                                  <div className={`mt-0.5 sm:mt-1 text-[11px] leading-tight ${dark ? "text-white/50" : "text-[#2d2c35]/60"}`}>
                                    {opt.note}
                                  </div>
                                </div>
                                <div className={`shrink-0 text-lg font-bold font-mono-num text-end sm:text-start sm:mt-1 sm:order-none ${dark ? "text-white" : "text-[#000000]"}`}>
                                  {formatMoney(opt.price || 0, currency, lang, digits).value} <span className="text-xs opacity-70">{formatMoney(0, currency, lang, digits).symbol}</span>
                                </div>
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Fine-tune slider */}
                    <div>
                      <div className={`mb-2 text-[11px] uppercase tracking-widest ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
                        {t.ai.customize}
                      </div>
                      <Slider
                        data-testid="price-slider"
                        min={Math.max(50, Math.round((ai.win_rate_price || 0) * 0.5))}
                        max={Math.round((ai.recommended_price || 0) * 1.5)}
                        step={10}
                        value={[finalPrice || 0]}
                        onValueChange={(v) => { setFinalPrice(v[0]); setPriceEdited(true); }}
                        className="mt-1"
                      />
                      <div className="flex items-center gap-2 mt-3">
                        <Button
                          data-testid="price-decrement"
                          variant="outline"
                          size="icon"
                          onClick={() => { setFinalPrice(Math.max(10, (finalPrice || 0) - 25)); setPriceEdited(true); }}
                          className={`h-9 w-9 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
                        >−25</Button>
                        <Input
                          data-testid="price-input"
                          type="text"
                          inputMode="numeric"
                          value={digits === "ar" ? localizeDigits(String(finalPrice || 0), "ar") : (finalPrice || 0)}
                          onChange={(e) => {
                            const v = normalizeDigits(e.target.value);
                            setFinalPrice(Number(v) || 0);
                            setPriceEdited(true);
                          }}
                          className={`h-9 flex-1 text-center font-mono-num font-bold ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white border-[#e5e5ea]"}`}
                        />
                        <Button
                          data-testid="price-increment"
                          variant="outline"
                          size="icon"
                          onClick={() => { setFinalPrice((finalPrice || 0) + 25); setPriceEdited(true); }}
                          className={`h-9 w-9 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
                        >+25</Button>
                      </div>
                    </div>


                    {/* Account cues — logged-in users see a silent "saved to your account" confirmation badge.
                        Guests see a soft upsell to sign up. */}
                    {authUser ? (
                      <div
                        data-testid="auto-save-badge"
                        className="mt-2 text-center text-[11px] text-[#18c98f]/80 inline-flex items-center justify-center gap-1.5 w-full"
                      >
                        <Check className="h-3 w-3" />
                        {lang === "ar"
                          ? "تم حفظ العرض تلقائيًا في حسابك (يُحفظ لمدة ٣٠ يومًا)"
                          : "Auto-saved to your account (kept 30 days)"}
                        <button
                          onClick={() => navigate("/me")}
                          className="underline hover:text-[#18c98f] font-bold"
                        >
                          {lang === "ar" ? "عرض السجلّ" : "View history"}
                        </button>
                      </div>
                    ) : (
                      <button
                        data-testid="save-proposal-signin-btn"
                        onClick={() => { setAuthModalMode("signup"); setAuthModalOpen(true); }}
                        className={`mt-2 text-center text-[11px] w-full inline-flex items-center justify-center gap-1 ${dark ? "text-[#18c98f]/80" : "text-[#0e8a62]"} hover:underline`}
                      >
                        <UserPlus className="h-3 w-3" />
                        {lang === "ar"
                          ? "سجّل مجانًا لحفظ عروضك تلقائيًا + ٣ اقتراحات 🎁"
                          : "Sign up to auto-save your quotes + 3 free credits"}
                      </button>
                    )}

                    <div className={`rounded-xl p-4 text-sm leading-relaxed ${dark ? "bg-[#1f2a47]/60 text-white/90 border border-[#2e3a5c]" : "bg-[#f3f8ff] text-[#2d2c35] border border-[#e5e5ea]"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                      <div className="font-bold mb-1 text-[#18c98f]">{t.ai.reasoning}</div>
                      <div>{ai.reasoning}</div>
                      {Array.isArray(ai.tips) && ai.tips.length > 0 && (
                        <>
                          <div className="font-bold mt-3 mb-1 text-[#18c98f]">{t.ai.tips}</div>
                          <ul className="list-disc ms-5 space-y-1">
                            {ai.tips.map((tip, i) => <li key={i}>{tip}</li>)}
                          </ul>
                        </>
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* 5-star rating feedback — shown after AI result is received */}
              {ai && !aiLoading && (
                <RatingFeedback
                  lang={lang}
                  theme={theme}
                  context="ai_suggestion"
                  serviceType={service}
                  email={email}
                />
              )}

              {(rateBlocked || (rateInfo && rateInfo.remaining <= 0 && paidCredits <= 0 && !rateInfo?.subscription_bypass)) && (
                <div className={`mt-4 rounded-xl p-4 border ${dark ? "bg-red-950/40 border-red-500/40 text-red-100" : "bg-red-50 border-red-300 text-red-900"}`}>
                  <div className="font-bold text-sm mb-1">{t.ai.rateBlockedTitle}</div>
                  <div className="text-sm leading-relaxed mb-3">
                    {rateBlocked
                      ? (lang === "ar" ? rateBlocked.message_ar : rateBlocked.message_en)
                      : (lang === "ar"
                          ? `لقد استخدمت الحد الأقصى (${rateInfo.limit}) اقتراحات أسعار لهذا الشهر. سيتجدد الحد تلقائيًا بعد ٣٠ يومًا من أول طلب لك.`
                          : `You have reached the monthly limit of ${rateInfo.limit} price suggestions. Your quota will reset automatically 30 days after your first request.`)}
                  </div>
                  {rateBlocked?.code !== "sub_daily_cap" && (
                    <Button
                      data-testid="buy-credits-button"
                      onClick={() => openBuy("result_panel")}
                      className="w-full h-10 bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold rounded-full gap-2"
                    >
                      <ShoppingCart className="h-4 w-4" />
                      {lang === "ar" ? "شراء اقتراحات أسعار إضافية — باقات من ١٩ ريال" : "Buy more price suggestions — plans from 19 SAR"}
                    </Button>
                  )}
                </div>
              )}

              <Button
                data-testid="ai-recalculate-btn"
                onClick={requestAI}
                disabled={aiLoading || (rateInfo && rateInfo.remaining <= 0 && paidCredits <= 0 && !rateInfo?.subscription_bypass) || rateBlocked?.code === "sub_daily_cap"}
                className="mt-6 w-full bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold h-12 rounded-full gap-2 disabled:opacity-60 hidden"
              >
                {aiLoading ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                {aiLoading ? t.ai.loading : (ai ? (lang === "ar" ? "إعادة الحساب بالذكاء الاصطناعي" : "Recalculate with AI") : t.ai.button)}
              </Button>

              {(rateInfo || paidCredits > 0 || subscription) && (
                <div data-testid="ai-quota" className={`mt-3 text-center text-[11px] ${dark ? "text-white/60" : "text-[#2d2c35]/70"}`}>
                  {(subscription || rateInfo?.subscription_bypass) ? (
                    <span className="inline-flex items-center gap-1.5 flex-wrap justify-center">
                      <Crown className="h-3 w-3 text-[#18c98f]" />
                      <span className="font-bold text-[#18c98f]">
                        {(subscription?.plan || rateInfo?.subscription_plan) === "yearly_unlimited"
                          ? (lang === "ar" ? "اشتراك سنوي · لامحدود ∞" : "Yearly · Unlimited ∞")
                          : (lang === "ar" ? "اشتراك شهري · لامحدود ∞" : "Monthly · Unlimited ∞")}
                      </span>
                      {subscription?.days_remaining != null && (
                        <span className={`${dark ? "text-white/55" : "text-[#2d2c35]/60"}`}>
                          {lang === "ar" ? `· متبقّي ${subscription.days_remaining} يومًا` : `· ${subscription.days_remaining} days left`}
                        </span>
                      )}
                    </span>
                  ) : paidCredits > 0 ? (
                    <span className="inline-flex items-center gap-1">
                      <Zap className="h-3 w-3 text-[#18c98f]" />
                      <span className="font-bold text-[#18c98f]">
                        {lang === "ar" ? `رصيدك المدفوع: ${paidCredits} اقتراح سعر` : `Paid balance: ${paidCredits} price credits`}
                      </span>
                    </span>
                  ) : (
                    <>
                      {t.ai.quotaLeft}: <span className="font-mono-num font-bold">{Math.max(0, rateInfo.remaining)}</span> {t.ai.quotaOf} <span className="font-mono-num">{rateInfo.limit}</span>
                      {" · "}
                      <button
                        data-testid="buy-more-link"
                        onClick={() => openBuy("quota_block")}
                        className="text-[#18c98f] hover:underline font-semibold"
                      >
                        {lang === "ar" ? "شراء المزيد" : "Buy more"}
                      </button>
                      {!bonusClaimed && (
                        <>
                          {" · "}
                          <button
                            data-testid="open-bonus-modal-inline"
                            onClick={() => setBonusOpen(true)}
                            className="inline-flex items-center gap-1 text-[#18c98f] hover:underline font-extrabold"
                          >
                            <span>{lang === "ar" ? "+٣ اقتراحات أسعار مجانًا بالتقييم" : "+3 free price suggestions with rating"}</span>
                          </button>
                        </>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      {!IS_EMBED && (
        <section data-testid="price-guide-section" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-2">
          <HeroQuickStart lang={lang} dark={dark} t={t} onPick={quickPick} />
        </section>
      )}

      {/* Integrations modal */}
      <IntegrationsModal
        open={integrateOpen}
        onOpenChange={setIntegrateOpen}
        lang={lang}
        theme={theme}
        tt={t.integrate}
      />

      {/* Payments modal */}
      <PaymentsModal
        open={paymentsOpen}
        onOpenChange={setPaymentsOpen}
        lang={lang}
        theme={theme}
        defaultEmail={email}
      />

      {/* JWT + Google sign-in modal */}
      <AuthModal
        open={authModalOpen}
        onClose={() => setAuthModalOpen(false)}
        lang={lang}
        initialMode={authModalMode}
      />


      {/* Bonus feedback modal — grants +3 free credits for a complete rating + comment */}
      <BonusFeedbackModal
        open={bonusOpen}
        onOpenChange={setBonusOpen}
        lang={lang}
        theme={theme}
        serviceType={service}
        onCreditsGranted={(info) => {
          if (info?.email) {
            setEmail(info.email);
            try { localStorage.setItem("pl_email", info.email); } catch {}
          }
          if (typeof info?.credits_remaining === "number") {
            setPaidCredits(info.credits_remaining);
          }
          setBonusClaimed(true);
        }}
      />

      {/* Exit-intent upsell modal — audience-aware content before the user leaves */}
      <ExitIntentModal
        open={exitOpen}
        onOpenChange={setExitOpen}
        lang={lang}
        theme={theme}
        variant={exitVariant}
        onPrimary={(v) => {
          if (v === "share") {
            // Native share if available, else WhatsApp fallback.
            const url = "https://prolancer.app/";
            const text = lang === "ar"
              ? "جرّب حاسبة برولانسر لتسعير خدمات العمل الحر بالريال السعودي:"
              : "Try the Prolancer Calculator for Saudi freelance pricing in SAR:";
            if (navigator.share) {
              navigator.share({ title: "Prolancer", text, url }).catch(() => {});
            } else {
              window.open(
                `https://wa.me/?text=${encodeURIComponent(text + " " + url)}`,
                "_blank",
                "noopener,noreferrer"
              );
            }
            return;
          }
          // Both "subscribe" and "yearly" → open payments modal / pricing section.
          try { window.location.hash = "#pricing"; } catch {}
          const el = document.querySelector('[data-testid="pricing-plans-section"]');
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }}
      />

      {/* Floating pill — always visible until the bonus is claimed */}
      {!IS_EMBED && !bonusClaimed && !subscription && (
        <button
          data-testid="bonus-floating-cta"
          onClick={() => setBonusOpen(true)}
          dir={lang === "ar" ? "rtl" : "ltr"}
          className={`no-print fixed z-40 bottom-5 ${lang === "ar" ? "left-5" : "right-5"} inline-flex items-center gap-2 px-4 py-3 rounded-full bg-[#18c98f] text-white text-sm font-extrabold shadow-[0_12px_30px_-10px_rgba(24,201,143,0.75)] hover:brightness-110 hover:scale-105 active:scale-95 transition-all ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
          aria-label={lang === "ar" ? "احصل على ٣ اقتراحات أسعار مجانًا بالتقييم" : "Get 3 free price suggestions by rating"}
        >
          <span aria-hidden="true" className="inline-flex items-center justify-center h-6 w-6 rounded-full bg-[#182138] text-[#18c98f] text-[11px] font-black">+3</span>
          <span className="hidden sm:inline">
            {lang === "ar" ? "اقتراحات أسعار مجانًا بالتقييم" : "Free price suggestions · rate us"}
          </span>
          <span className="sm:hidden">
            {lang === "ar" ? "مجانًا" : "Free"}
          </span>
        </button>
      )}

      {/* Account modal */}
      <AccountModal
        open={accountOpen}
        onOpenChange={(v) => { setAccountOpen(v); if (!v) setAccountMode("user"); }}
        lang={lang}
        theme={theme}
        email={email}
        setEmail={setEmail}
        onBuyMore={() => openBuy("account")}
        initialMode={accountMode}
      />

      {/* Proposal (PDF export) modal */}
      <ProposalModal
        open={proposalOpen}
        onOpenChange={setProposalOpen}
        lang={lang}
        theme={theme}
        serviceLabel={customServiceName.trim() || t.services[service]}
        experienceLabel={t.experience[experience]}
        complexityLabel={t.complexity[complexity]}
        unitLabel={t.form[`unit${unit.charAt(0).toUpperCase()}${unit.slice(1)}`]}
        quantity={quantity}
        revisions={revisions}
        rush={rush}
        description={description}
        price={convertFromSar(ai && finalPrice ? finalPrice : (result?.avgPrice || 0), currency)}
        rushFee={convertFromSar(result?.rushFee || 0, currency)}
        currencySymbol={formatMoney(0, currency, lang).symbol}
        scopeLines={scopeToLines(service, scopeDetails[service], lang)}
        priceSource={ai && finalPrice ? "ai" : "estimate"}
      />

      {/* Rates editor modal */}
      <Dialog open={ratesOpen} onOpenChange={setRatesOpen}>
        <DialogContent
          data-testid={CALC.ratesModal}
          className={`max-w-2xl max-h-[90vh] overflow-y-auto ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white" : "bg-white"}`}
          dir={lang === "ar" ? "rtl" : "ltr"}
        >
          <DialogHeader>
            <DialogTitle className={`text-xl font-bold flex items-center gap-2 ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              <Settings2 className="h-5 w-5 text-[#18c98f]" />
              {t.rates.title}
            </DialogTitle>
            <DialogDescription className={`${dark ? "text-white/70" : "text-[#2d2c35]/70"} ${lang === "ar" ? "font-arabic text-right" : ""}`}>
              {t.rates.desc}
            </DialogDescription>
          </DialogHeader>

          {/* How-to callout */}
          <div className={`rounded-xl p-4 border ${dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-[#f3f8ff] border-[#e5e5ea]"} mt-1`}>
            <div className={`text-xs font-bold uppercase tracking-widest mb-2 text-[#18c98f] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              {t.rates.howTo}
            </div>
            <ul className={`space-y-1.5 text-[13px] leading-relaxed ${dark ? "text-white/85" : "text-[#2d2c35]"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              {t.rates.howToItems.map((item, i) => (
                <li key={i} className="flex items-start gap-2">
                  <Check className="h-3.5 w-3.5 text-[#18c98f] shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* Column headers — hidden on mobile (stacked layout below) */}
          <div className={`hidden sm:grid grid-cols-4 gap-2 items-center px-1 pt-3 pb-1 text-[10px] uppercase tracking-widest font-bold ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>
            <div className="col-span-1">{lang === "ar" ? "الخدمة" : "Service"}</div>
            <div className="text-center text-[#18c98f]">{t.rates.minCol}</div>
            <div className="text-center text-[#18c98f]">{t.rates.avgCol}</div>
            <div className="text-center text-[#18c98f]">{t.rates.maxCol}</div>
          </div>
          <div className={`text-[10px] mb-2 text-end ${dark ? "text-white/50" : "text-[#2d2c35]/50"}`}>
            ({t.rates.unitLabel})
          </div>

          <div className="max-h-[55vh] sm:max-h-[40vh] overflow-y-auto space-y-3 sm:space-y-2 py-1">
            {SERVICE_LIST.map((s) => (
              <div
                key={s.id}
                className={`rounded-xl p-3 sm:p-0 sm:rounded-lg sm:grid sm:grid-cols-4 sm:gap-2 sm:items-center sm:px-2 sm:py-1.5 border sm:border-0 ${dark ? "bg-[#1f2a47] border-[#2e3a5c] sm:bg-transparent sm:hover:bg-[#1f2a47]" : "bg-[#f9f9fa] border-[#e5e5ea] sm:bg-transparent sm:hover:bg-[#f3f8ff]"}`}
              >
                <div className={`text-sm font-bold mb-2 sm:mb-0 sm:font-semibold ${lang === "ar" ? "font-arabic" : "font-outfit"} ${dark ? "text-white" : "text-[#2d2c35]"}`}>
                  {t.services[s.id]}
                </div>
                <div className="grid grid-cols-3 gap-2 sm:contents">
                  {["min", "avg", "max"].map((k) => (
                    <div key={k} className="flex flex-col sm:contents">
                      <span className={`sm:hidden text-[10px] font-bold uppercase tracking-widest mb-1 text-[#18c98f]`}>
                        {t.rates[`${k}Col`]}
                      </span>
                      <Input
                        type="text"
                        inputMode="numeric"
                        value={digits === "ar" ? localizeDigits(String(editingRates[s.id][k]), "ar") : editingRates[s.id][k]}
                        onChange={(e) => {
                          const v = normalizeDigits(e.target.value);
                          setEditingRates({
                            ...editingRates,
                            [s.id]: { ...editingRates[s.id], [k]: Number(v) || 0 },
                          });
                        }}
                        className={`h-11 sm:h-10 font-mono-num text-center ${dark ? "bg-[#1f2a47] border-[#2e3a5c] text-white sm:bg-[#1f2a47]" : "bg-white border-[#e5e5ea]"}`}
                        data-testid={`rate-${s.id}-${k}`}
                        dir="ltr"
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <DialogFooter className="gap-2">
            <Button
              data-testid={CALC.ratesResetBtn}
              variant="outline"
              onClick={resetRates}
              className={`gap-2 ${dark ? "border-[#2e3a5c] bg-[#1f2a47] text-white hover:bg-[#2e3a5c]" : ""}`}
            >
              <RefreshCw className="h-4 w-4" />
              {t.rates.reset}
            </Button>
            <Button
              data-testid={CALC.ratesSaveBtn}
              onClick={saveRates}
              className="bg-gradient-to-r from-[#18c98f] to-[#12a777] hover:from-[#12a777] hover:to-[#0e8a62] text-[#1f2a47] font-bold"
            >
              {t.rates.save}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Footer */}
      {!IS_EMBED && (
        <>
          {/* Explicit section wrapper so each tool is visually distinct and anchor-targetable. */}
          <div id="earnings-tool" data-testid="earnings-section-wrapper" className="relative p-scroll-anchor mt-6 sm:mt-8">
            <div data-testid="earnings-tool-frame" className="p-tool-frame mx-3 sm:mx-5 lg:mx-8">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5">
              <div data-testid="earnings-tool-chip" className={`p-tool-chip ${lang === "ar" ? "is-ar font-arabic" : "font-outfit"}`}>
                {lang === "ar" ? "حاسبة الدخل" : "Earnings Calculator"}
              </div>
            </div>
            <EarningsCalculator lang={lang} theme={theme} currency={currency} setCurrency={setCurrency} digits={digits} />
            </div>
          </div>

          {/* Integration tools — moved above the feedback widget per user request. */}
          {!IS_EMBED && (
            <div id="integrate-tool" data-testid="integrate-section-wrapper" className="relative p-scroll-anchor mt-6 sm:mt-8">
              <div data-testid="integrate-tool-frame" className="p-tool-frame mx-3 sm:mx-5 lg:mx-8">
                <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-4 sm:pt-5">
                  <div data-testid="integrate-tool-chip" className={`p-tool-chip ${lang === "ar" ? "is-ar font-arabic" : "font-outfit"}`}>
                    {lang === "ar" ? "أدوات التكامل" : "Integration Tools"}
                  </div>
                </div>
                <IntegrationCTA lang={lang} theme={theme} />
              </div>
            </div>
          )}

          {/* Compact "Rate Prolancer" widget — placed AFTER the integration tools
              per the user's requested order. Re-engineered to one tight card. */}
          {!IS_EMBED && (
            <section
              data-testid="standalone-feedback-section"
              className={`${dark ? "bg-[#182138]" : "bg-white"}`}
              dir={lang === "ar" ? "rtl" : "ltr"}
            >
              <div className="max-w-xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
                <div
                  className={`rounded-2xl border p-3.5 sm:p-4 ${
                    dark
                      ? "bg-[#1f2a47] border-[#2e3a5c]"
                      : "bg-[#f3f8ff] border-[#e5e5ea]"
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className={`inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.25em] font-extrabold ${dark ? "text-[#18c98f]" : "text-[#12a777]"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                      <Sparkles className="h-3.5 w-3.5" />
                      {lang === "ar" ? "شاركنا رأيك" : "Your feedback"}
                    </div>
                    {!bonusClaimed && !subscription && (
                      <button
                        data-testid="standalone-bonus-cta"
                        onClick={() => setBonusOpen(true)}
                        className={`inline-flex items-center gap-1 rounded-full bg-[#18c98f] text-white text-[10px] font-black px-2.5 py-1 shadow-[0_4px_12px_-4px_rgba(24,201,143,0.6)] hover:bg-[#18c98f] transition ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
                      >
                        <Gift className="h-3 w-3" />
                        {lang === "ar" ? "+٣ مجاني" : "+3 free"}
                      </button>
                    )}
                    {bonusClaimed && (
                      <span className={`inline-flex items-center gap-1 rounded-full bg-[#18c98f]/15 border border-[#18c98f]/40 text-[#18c98f] text-[10px] font-black px-2 py-0.5 ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
                        <Gift className="h-3 w-3" />
                        {lang === "ar" ? "مُستلم" : "Claimed"}
                      </span>
                    )}
                  </div>
                  <RatingFeedback
                    lang={lang}
                    theme={theme}
                    context="general_feedback"
                    standalone={true}
                    compact
                  />
                </div>
              </div>
            </section>
          )}
        </>
      )}
      {/* Integration tools moved up above the feedback widget — removed from this position. */}

      {/* Service pricing guides section moved to its own route `/guides` (reachable from footer sitemap). */}

      {/* HelpfulResources section removed — the official-resource links now live
          inside the Footer sitemap grid as rectangular cards. */}

      {/* Embed-only footer bar — exit/back link so iframe/mobile browser viewers can return. */}
      {IS_EMBED && (
        <div data-testid="embed-footer" dir={lang === "ar" ? "rtl" : "ltr"} className="mt-4 mx-4 mb-6">
          <div
            data-testid="embed-exit-bar"
            className={`rounded-2xl border p-4 flex flex-col sm:flex-row items-center justify-between gap-3 ${
              dark
                ? "bg-[#1f2a47] border-[#2e3a5c] text-white"
                : "bg-[#f3f8ff] border-[#e5e5ea] text-[#182138]"
            }`}
          >
            <div className={`text-xs sm:text-sm ${dark ? "text-white/70" : "text-[#2d2c35]/80"} ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
              <a
                href="https://prolancer.app"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="embed-top-brand-link"
                className="font-extrabold text-[#18c98f] hover:underline"
              >
                {lang === "ar" ? "حاسبة برولانسر" : "Prolancer Calculator"}
              </a>
              <span className="mx-1 opacity-70">—</span>
              {lang === "ar"
                ? "خدمة مجانية للسوق السعودي 🇸🇦"
                : "a free gift to the Saudi market 🇸🇦"}
            </div>
            <button
              data-testid="embed-exit-btn"
              onClick={() => {
                try {
                  if (window.parent && window.parent !== window) {
                    window.parent.postMessage({ type: "prolancer:exit" }, "*");
                  } else if (window.history.length > 1) {
                    window.history.back();
                  } else {
                    window.close();
                  }
                } catch {
                  try { window.history.back(); } catch {}
                }
              }}
              className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm font-extrabold shrink-0 transition-all bg-[#18c98f] text-white hover:brightness-110 shadow-[0_6px_18px_-6px_rgba(24,201,143,0.6)] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
            >
              <ArrowLeft className="h-4 w-4" />
              {lang === "ar" ? "خروج ورجوع" : "Exit · back"}
            </button>
          </div>

          {/* Copyright + brand attribution */}
          <div
            data-testid="embed-copyright"
            className={`mt-3 rounded-2xl border p-3 flex flex-col sm:flex-row items-center justify-between gap-2 text-[11px] ${
              dark ? "bg-[#182138] border-[#2e3a5c] text-white/55" : "bg-white border-[#e5e5ea] text-[#2d2c35]/60"
            } ${lang === "ar" ? "font-arabic" : "font-outfit"}`}
          >
            <span className="inline-flex items-center gap-1.5">
              <span>©</span>
              <span>{new Date().getFullYear()}</span>
              <a
                href="https://prolancer.app"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="embed-copyright-brand-link"
                className="font-bold text-[#18c98f] hover:underline"
              >
                Prolancer / برولانسر
              </a>
              <span>·</span>
              <span>{lang === "ar" ? "جميع الحقوق محفوظة" : "All rights reserved"}</span>
            </span>
            <a
              data-testid="embed-brand-link"
              href="https://prolancer.app"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 font-bold text-[#18c98f] hover:underline"
            >
              prolancer.app
            </a>
          </div>
        </div>
      )}

      {!IS_EMBED && <ScrollTopFab lang={lang} theme={theme} />}
      {!IS_EMBED && <SeoFaq lang={lang} dark={dark} />}
      {!IS_EMBED && <Footer lang={lang} theme={theme} />}
    </div>
  );
}

function FormBlock({ title, children, dark, lang }) {
  return (
    <div className={`p-card p-3 sm:p-4 p-editable-hint`}>
      <h3 className={`mb-2.5 text-[11px] font-bold uppercase tracking-widest flex items-center gap-1.5 text-[#f97316] ${lang === "ar" ? "font-arabic" : "font-outfit"}`}>
        <span className="inline-block h-1.5 w-1.5 rounded-full bg-[#f97316] animate-pulse" />
        {title}
      </h3>
      {children}
    </div>
  );
}

function PillButton({ active, onClick, children, testId, dark }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`rounded-full text-xs sm:text-sm font-semibold py-1.5 px-2 border transition-all whitespace-nowrap text-center ${
        active
          ? "bg-[#18c98f] text-white border-[#18c98f] font-extrabold shadow-[0_6px_18px_-6px_rgba(24,201,143,0.7)]"
          : dark
          ? "bg-[#1f2a47] text-white border-[#2e3a5c] hover:border-[#18c98f]/60"
          : "bg-white text-[#2d2c35] border-[#e5e5ea] hover:border-[#18c98f]/60"
      }`}
    >
      {children}
    </button>
  );
}

function BreakdownRow({ label, value, dark, highlight }) {
  return (
    <div className={`flex items-center justify-between py-1.5 text-sm ${dark ? "text-white/85" : "text-[#2d2c35]/85"}`}>
      <span>{label}</span>
      <span className={`font-mono-num font-semibold ${highlight ? (dark ? "text-[#18c98f]" : "text-[#12a777]") : ""}`}>{value}</span>
    </div>
  );
}

function MetricCard({ label, value, dark, highlight }) {
  return (
    <div className={`rounded-xl p-3 border ${highlight ? (dark ? "bg-[#18c98f]/10 border-[#18c98f]/40" : "bg-[#18c98f]/10 border-[#18c98f]/40") : (dark ? "bg-[#1f2a47] border-[#2e3a5c]" : "bg-white border-[#e5e5ea]")}`}>
      <div className={`text-[11px] uppercase tracking-widest ${dark ? "text-white/60" : "text-[#2d2c35]/60"}`}>{label}</div>
      <div className={`mt-1 text-xl font-bold font-mono-num ${highlight ? (dark ? "text-[#18c98f]" : "text-[#12a777]") : (dark ? "text-white" : "text-[#000000]")}`}>{value}</div>
    </div>
  );
}
